import ReactGA from 'react-ga4';

// Verifica se está rodando em um ambiente como WebContainer/Stackblitz
// para evitar inicializar GA em ambientes de demonstração/teste online.
const isWebContainer = typeof window !== 'undefined' && window.location.hostname.includes('webcontainer');

// Flag para verificar se o GA foi inicializado com sucesso
let isGaInitialized = false;

/**
 * Inicializa o Google Analytics (GA4).
 * Deve ser chamado uma vez na inicialização da aplicação.
 * Apenas inicializa em ambiente de produção (não WebContainer).
 * @param {string} measurementId - O ID de medição do GA4 (ex: G-XXXXXXXXXX). Se não for fornecido, tentará usar VITE_GA_MEASUREMENT_ID.
 */
export const initGA = (measurementId?: string) => {
  const gaId = measurementId || import.meta.env.VITE_GA_MEASUREMENT_ID;

  // Não inicializa em WebContainers ou se não houver ID
  if (isWebContainer || !gaId) {
    if (!gaId && !isWebContainer) {
      console.warn('Google Analytics Measurement ID não encontrado. GA não será inicializado.');
    }
    return;
  }

  try {
    ReactGA.initialize(gaId);
    isGaInitialized = true;
    console.log('Google Analytics inicializado com ID:', gaId); // Log para confirmar inicialização
  } catch (error) {
    console.error('Falha ao inicializar Google Analytics:', error); // Usar console.error para erros
  }
};

/**
 * Rastreia uma visualização de página.
 * @param {string} path - O caminho da página a ser rastreado (ex: window.location.pathname + window.location.search).
 */
export const trackPageView = (path: string) => {
  if (!isGaInitialized) {
    return; // Não faz nada se GA não foi inicializado
  }
  try {
    ReactGA.send({ hitType: "pageview", page: path });
  } catch (error) {
    console.error(`Falha ao rastrear page view para ${path}:`, error); // Log mais específico
  }
};

/**
 * Função base para rastrear eventos customizados.
 * @param {string} category - Categoria do evento.
 * @param {string} action - Ação do evento.
 * @param {string} [label] - Rótulo opcional do evento.
 * @param {number} [value] - Valor numérico opcional do evento.
 */
export const trackEvent = (category: string, action: string, label?: string, value?: number) => {
  if (!isGaInitialized) {
    return; // Não faz nada se GA não foi inicializado
  }
  try {
    const eventData = { category, action, label, value };
    // Remove chaves com valores undefined para não enviar dados extras
    Object.keys(eventData).forEach(key => eventData[key] === undefined && delete eventData[key]);
    ReactGA.event(eventData);
  } catch (error) {
     console.error('Falha ao rastrear evento:', { category, action, label, value }, error);
  }
};

// --- Funções Auxiliares para Eventos Específicos ---
//    (Mantidas como estavam, pois são úteis e usam trackEvent)

/** Rastreia interações gerais do usuário. */
export const trackInteraction = (action: string, label?: string) => {
  trackEvent('User Interaction', action, label);
};

/** Rastreia downloads de documentos. */
export const trackDownload = (documentType: string, documentId: string, documentTitle: string) => {
  // Considerar usar documentId ou documentTitle como label se fizer sentido
  trackEvent('Document Download', documentType, documentTitle);
};

/** Rastreia buscas realizadas no site. */
export const trackSearch = (searchTerm: string, resultsCount: number) => {
  trackEvent('Search', 'Query', searchTerm, resultsCount);
};

/** Rastreia submissões de formulários. */
export const trackFormSubmission = (formName: string, success: boolean) => {
  trackEvent('Form Submission', formName, success ? 'Success' : 'Failed');
};

/** Rastreia interações com redes sociais. */
export const trackSocialInteraction = (network: string, action: string) => {
  trackEvent('Social', action, network);
};

/** Rastreia cliques em links externos. */
export const trackOutboundLink = (url: string, category: string = 'Outbound Link') => {
  trackEvent(category, 'Click', url);
};

